import React, { useCallback, useRef, useState } from "react";
import { mmToPx, pxToMm } from "../utils/labelSizes";
import ElementRenderer from "./ElementRenderer";

const getElementMinSize = (element) => {
  switch (element.type) {
    case "nutrition":
      return { minWidth: 30, minHeight: 28 };
    case "image":
      return { minWidth: 8, minHeight: 8 };
    default:
      return { minWidth: 4, minHeight: 4 };
  }
};

// Interactive wrapper used only inside the Template Designer canvas.
// Supports move (drag), resize (8 handles) and rotate (top handle).
const DesignerCanvasElement = ({
  element,
  isSelected,
  onSelect,
  onChange,
  onCommitChange,
  onInteractionStart,
  dataMap,
  zoom = 1,
  canvasWidthMm = Infinity,
  canvasHeightMm = Infinity,
}) => {
  const dragState = useRef(null);
  const elementRef = useRef(null);
  const textEditorRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [draftText, setDraftText] = useState(element.content || "");

  const clampX = useCallback(
    (x, width) => Math.min(Math.max(0, x), Math.max(0, canvasWidthMm - width)),
    [canvasWidthMm],
  );
  const clampY = useCallback(
    (y, height) =>
      Math.min(Math.max(0, y), Math.max(0, canvasHeightMm - height)),
    [canvasHeightMm],
  );

  const beginDrag = () => {
    setIsDragging(true);
    // Prevent the browser from selecting page text while dragging over the canvas.
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const commitInlineText = useCallback(() => {
    const nextText = draftText;
    setIsEditingText(false);
    if (nextText === (element.content || "")) return;
    onCommitChange?.(element.id, { content: nextText });
  }, [draftText, element.content, element.id, onCommitChange]);

  const handleMouseDownMove = useCallback(
    (e) => {
      if (element.locked || isEditingText) return;
      e.stopPropagation();
      onSelect(element.id);
      dragState.current = {
        mode: "move",
        startX: e.clientX,
        startY: e.clientY,
        origX: element.x,
        origY: element.y,
      };
      beginDrag();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element, isEditingText],
  );

  const handleMouseDownResize = useCallback(
    (handle) => (e) => {
      if (element.locked || isEditingText) return;
      e.stopPropagation();
      e.preventDefault();
      onSelect(element.id);
      dragState.current = {
        mode: "resize",
        handle,
        startX: e.clientX,
        startY: e.clientY,
        origX: element.x,
        origY: element.y,
        origWidth: element.width,
        origHeight: element.height,
      };
      beginDrag();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element, isEditingText],
  );

  const handleMouseDownRotate = useCallback(
    (e) => {
      if (element.locked || isEditingText) return;
      e.stopPropagation();
      e.preventDefault();
      onSelect(element.id);
      const rect = elementRef.current.getBoundingClientRect();
      dragState.current = {
        mode: "rotate",
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
      };
      beginDrag();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element, isEditingText],
  );

  const handleMouseMove = (e) => {
    const state = dragState.current;
    if (!state) return;

    if (!state.hasStartedInteraction) {
      onInteractionStart?.();
      state.hasStartedInteraction = true;
    }

    if (state.mode === "move") {
      const deltaXMm = pxToMm((e.clientX - state.startX) / zoom);
      const deltaYMm = pxToMm((e.clientY - state.startY) / zoom);
      onChange(element.id, {
        x: Math.round(clampX(state.origX + deltaXMm, element.width) * 10) / 10,
        y: Math.round(clampY(state.origY + deltaYMm, element.height) * 10) / 10,
      });
    } else if (state.mode === "resize") {
      const deltaXMm = pxToMm((e.clientX - state.startX) / zoom);
      const deltaYMm = pxToMm((e.clientY - state.startY) / zoom);
      const handle = state.handle;
      const { minWidth, minHeight } = getElementMinSize(element);
      let { origX, origY, origWidth, origHeight } = state;
      let newX = origX;
      let newY = origY;
      let newWidth = origWidth;
      let newHeight = origHeight;

      const hasE = handle.includes("e");
      const hasW = handle.includes("w");
      const hasS = handle.includes("s");
      const hasN = handle.includes("n");

      if (hasE) newWidth = Math.max(minWidth, origWidth + deltaXMm);
      if (hasS) newHeight = Math.max(minHeight, origHeight + deltaYMm);
      if (hasW) {
        newWidth = Math.max(minWidth, origWidth - deltaXMm);
        newX = origX + (origWidth - newWidth);
      }
      if (hasN) {
        newHeight = Math.max(minHeight, origHeight - deltaYMm);
        newY = origY + (origHeight - newHeight);
      }

      // Keep the element fully inside the canvas while resizing.
      newX = Math.max(0, newX);
      newY = Math.max(0, newY);
      newWidth = Math.min(newWidth, canvasWidthMm - newX);
      newHeight = Math.min(newHeight, canvasHeightMm - newY);

      onChange(element.id, {
        x: Math.round(newX * 10) / 10,
        y: Math.round(newY * 10) / 10,
        width: Math.round(newWidth * 10) / 10,
        height: Math.round(newHeight * 10) / 10,
      });
    } else if (state.mode === "rotate") {
      const angleRad = Math.atan2(
        e.clientY - state.centerY,
        e.clientX - state.centerX,
      );
      let angleDeg = angleRad * (180 / Math.PI) + 90; // handle sits above center (pointing up = 0deg)
      angleDeg = Math.round(angleDeg);
      onChange(element.id, { rotation: angleDeg });
    }
  };

  const handleMouseUp = () => {
    dragState.current = null;
    setIsDragging(false);
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleDoubleClick = useCallback(
    (e) => {
      if (element.type !== "text" || element.locked) return;
      e.stopPropagation();
      e.preventDefault();
      onSelect(element.id);
      setDraftText(element.content || "");
      setIsEditingText(true);
      window.requestAnimationFrame(() => {
        if (!textEditorRef.current) return;
        textEditorRef.current.focus();
        textEditorRef.current.select();
      });
    },
    [element, onSelect],
  );

  const textEditorStyle = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    padding: `${element.padding || 0}px`,
    margin: 0,
    border: "1px solid #0aad0a",
    outline: "none",
    resize: "none",
    overflow: "hidden",
    boxSizing: "border-box",
    backgroundColor:
      element.backgroundColor && element.backgroundColor !== "transparent"
        ? element.backgroundColor
        : "rgba(255,255,255,0.92)",
    color: element.color || "#000000",
    fontFamily: element.fontFamily || "Poppins, Noto Sans, Arial, sans-serif",
    fontSize: `${element.fontSize || 12}px`,
    fontWeight: element.bold ? "bold" : element.fontWeight || "normal",
    fontStyle: element.italic ? "italic" : "normal",
    textDecoration: element.underline ? "underline" : "none",
    textAlign: element.textAlign || "left",
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  };

  const RESIZE_HANDLES = [
    { key: "nw", top: -6, left: -6, cursor: "nwse-resize" },
    { key: "n", top: -6, left: "50%", cursor: "ns-resize", centerX: true },
    { key: "ne", top: -6, right: -6, cursor: "nesw-resize" },
    { key: "e", top: "50%", right: -6, cursor: "ew-resize", centerY: true },
    { key: "se", bottom: -6, right: -6, cursor: "nwse-resize" },
    { key: "s", bottom: -6, left: "50%", cursor: "ns-resize", centerX: true },
    { key: "sw", bottom: -6, left: -6, cursor: "nesw-resize" },
    { key: "w", top: "50%", left: -6, cursor: "ew-resize", centerY: true },
  ];

  return (
    <div
      ref={elementRef}
      onMouseDown={handleMouseDownMove}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(element.id);
      }}
      style={{
        position: "absolute",
        left: mmToPx(element.x),
        top: mmToPx(element.y),
        width: mmToPx(element.width),
        height: mmToPx(element.height),
        transform: `rotate(${element.rotation || 0}deg)`,
        transformOrigin: "center center",
        cursor: element.locked ? "not-allowed" : "move",
        outline: isSelected ? "2px solid #0aad0a" : "none",
        outlineOffset: 1,
        zIndex: isDragging ? 9999 : isSelected ? 999 : element.zIndex || 0,
        boxShadow: isDragging ? "0 4px 16px rgba(0,0,0,0.25)" : "none",
      }}
    >
      {isEditingText ? (
        <textarea
          ref={textEditorRef}
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          onBlur={commitInlineText}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setDraftText(element.content || "");
              setIsEditingText(false);
              return;
            }
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commitInlineText();
            }
          }}
          style={textEditorStyle}
        />
      ) : (
        <ElementRenderer element={element} dataMap={dataMap} />
      )}
      {isSelected && !element.locked && !isEditingText && (
        <>
          {RESIZE_HANDLES.map((h) => (
            <div
              key={h.key}
              onMouseDown={handleMouseDownResize(h.key)}
              title="Resize"
              style={{
                position: "absolute",
                top: h.top,
                left: h.left,
                right: h.right,
                bottom: h.bottom,
                width: 10,
                height: 10,
                marginLeft: h.centerX ? -5 : 0,
                marginTop: h.centerY ? -5 : 0,
                background: "#fff",
                border: "2px solid #0aad0a",
                borderRadius: "50%",
                cursor: h.cursor,
                boxSizing: "border-box",
              }}
            />
          ))}
          <div
            onMouseDown={handleMouseDownRotate}
            title="Rotate"
            style={{
              position: "absolute",
              left: "50%",
              top: -24,
              width: 10,
              height: 10,
              marginLeft: -5,
              background: "#ff9800",
              borderRadius: "50%",
              cursor: "grab",
            }}
          />
        </>
      )}
    </div>
  );
};

export default DesignerCanvasElement;
